import { useMutation } from '@apollo/client';
import ConfirmationModal from 'components/ConfirmationModal';
import { EmptyStateWithText } from 'components/EmptyState';
import AsideListItem from 'components/Layout/components/BaseDataAside/AsideListItem';
import { useContextBaseDataAside } from 'components/Layout/components/BaseDataAside/context';
import { BaseDataForm } from 'components/Layout/components/BaseDataAside/submenu';
import { TabPane } from 'components/Layout/components/common/styled-components';
import { useContextLayout } from 'components/Layout/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import routePaths from 'constants/route-paths';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import { InsuranceListData } from 'services/insurances';
import { DELETE_INSURANCE } from 'services/mutation/insurances';

const BaseDataInsuranceAsideMenu = () => {
  const { t } = useTranslation();
  const tabPaneRef = useRef(null);
  const { setActiveUser, setActiveMenu } = useContextBaseDataAside();
  const location = useLocation();
  const history = useHistory();
  const { filteredInsurances, setShouldRefetch } = useContextLayout();

  const { showApiError } = useContextSnackbar();

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceListData | undefined>();

  const [deleteInsurance] = useMutation(DELETE_INSURANCE);

  const userPaths = matchPath(location.pathname, {
    path: routePaths.DATA_INSURANCE,
  }) as { params: { userId: string } };

  const pathName = location.pathname;

  useEffect(() => {
    const params = {
      userId: userPaths?.params?.userId,
    } as { userId: string };

    if (params.userId) {
      setActiveUser(params.userId);
      if (pathName.includes(routePaths.DATA_ACCESS)) {
        setActiveMenu(BaseDataForm.SYSTEM_ACCESS);
      } else if (pathName.includes(routePaths.DATA_CONTRACT)) {
        setActiveMenu(BaseDataForm.CONTRACT);
      } else {
        setActiveMenu(BaseDataForm.GENERAL);
      }
    }
  }, [setActiveMenu, setActiveUser, pathName, userPaths]);

  const onDelete = (insurance: InsuranceListData) => {
    const name = `${insurance.name}`;
    setConfirmationText(t('insurance_deleteConfirmation', { name }));
    setShowConfirmation(true);
    setSelectedInsurance(insurance);
  };

  const handleClose = (response: boolean) => {
    setShowConfirmation(false);
    if (!response || !selectedInsurance) {
      setSelectedInsurance(undefined);
      return;
    }
    deleteInsurance({ variables: { id: selectedInsurance.id } })
      .then(() => {
        setShouldRefetch('deleted');
        history.push(routePaths.BASE_DATA);
      })
      .catch((error) => {
        showApiError(error);
      });
  };

  return (
    <>
      <TabPane ref={tabPaneRef}>
        <div className="aside-menu">
          <ul className="menu-nav py-0">
            {filteredInsurances.length ? (
              filteredInsurances.map((insurance: InsuranceListData) => (
                <AsideListItem
                  key={insurance.id}
                  id={insurance.id}
                  label={insurance.name}
                  description=""
                  warning={1}
                  tabPaneRef={tabPaneRef}
                  routePath={routePaths.DATA_INSURANCE}
                  showDelete={true}
                  isDeleted={!!insurance.deletedAt}
                  onDelete={() => onDelete(insurance)}
                />
              ))
            ) : (
              <EmptyStateWithText text={t('no_results_for_current_settings')} verticalPositionTop />
            )}
          </ul>
        </div>
      </TabPane>
      <ConfirmationModal show={showConfirmation} text={confirmationText} onResponse={handleClose} />
    </>
  );
};

export default BaseDataInsuranceAsideMenu;
